import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Megaphone, HandHeart, HelpingHand, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import { useNotifications } from '../contexts/NotificationContext';
import { useHubT } from './safetyhub/hubStrings';
import { timeAgo, SEVERITY_STYLES } from './safetyhub/IncidentCard';

// Homepage / hub emergency banner. Shows the most severe active incident that
// affects the user's followed towns; critical incidents show for everyone.
export const EmergencyBanner = ({ compact = false }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const hubT = useHubT();
  const { preferences } = useNotifications();
  const [incidents, setIncidents] = useState([]);

  const towns = (preferences?.areaSubscriptions || []).join(',');

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/incidents/active`, { params: towns ? { towns } : {} })
      .then(r => { if (alive) setIncidents(r.data.incidents || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [towns]);

  if (incidents.length === 0) return null;
  const top = incidents[0];
  const others = incidents.length - 1;

  return (
    <div
      className="w-full max-w-2xl bg-red-700 text-white rounded-2xl shadow-lg border border-red-500/60 overflow-hidden mb-4"
      data-testid="emergency-banner"
    >
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
          </span>
          <span className="text-[11px] font-extrabold uppercase tracking-wider">{hubT('emergencyBannerTitle')}</span>
          {top.severity !== 'normal' && (
            <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${top.severity === 'critical' ? 'bg-white text-red-700' : 'bg-orange-400 text-white'}`}>
              {hubT(`sev_${top.severity}`)}
            </span>
          )}
        </div>
        <p className="text-base font-bold leading-snug flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {top.title}{others > 0 ? ` (+${others})` : ''}
        </p>
        <p className="text-xs text-red-100 mt-0.5 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {hubT('lastOfficialUpdate')}: {timeAgo(top.lastOfficialUpdateAt || top.updatedAt, i18n.language)}
        </p>
      </div>
      <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'} border-t border-red-500/60`}>
        <button
          onClick={() => navigate(`/incidents/${top.id}`)}
          className="px-2 py-2.5 text-[11px] font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-1"
          data-testid="banner-view-incident"
        >
          <AlertTriangle className="h-3.5 w-3.5" />{hubT('viewIncident')}
        </button>
        <button
          onClick={() => navigate(`/incidents/${top.id}#updates`)}
          className="px-2 py-2.5 text-[11px] font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-1 border-l border-red-500/60"
          data-testid="banner-official-updates"
        >
          <Megaphone className="h-3.5 w-3.5" />{hubT('officialUpdates')}
        </button>
        <button
          onClick={() => navigate(`/incidents/${top.id}?compose=help_needed`)}
          className={`px-2 py-2.5 text-[11px] font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-1 ${compact ? '' : 'sm:border-l'} border-t sm:border-t-0 border-red-500/60`}
          data-testid="banner-request-help"
        >
          <HandHeart className="h-3.5 w-3.5" />{hubT('requestHelp')}
        </button>
        <button
          onClick={() => navigate(`/incidents/${top.id}?compose=help_offered`)}
          className="px-2 py-2.5 text-[11px] font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-1 border-l border-t sm:border-t-0 border-red-500/60"
          data-testid="banner-offer-help"
        >
          <HelpingHand className="h-3.5 w-3.5" />{hubT('offerHelp')}
        </button>
      </div>
    </div>
  );
};

export default EmergencyBanner;
